import { useState } from 'react';
import {
  ActionIcon,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@cowbell/ui/core';
import { useMooConfig } from '../../provider';
import type { ConversationRef } from '../../types';
import { stripMarkdown } from '../../utils/strip-markdown';
import { CheckIcon, CloseIcon, TrashIcon } from './icons';
import { relativeTime } from './relative-time';
import styles from './ShellDrawer.module.css';

export interface ConversationRowProps {
  /** Underscore-suffixed to avoid the React `ref` prop name. */
  readonly ref_: ConversationRef;
  readonly agentName: string;
  readonly onOpen: () => void;
  readonly onDelete: () => void;
}

/** Single conversation entry. Hover (or focus) reveals the trash icon;
 *  click flips the row into an inline confirm state. Click confirm to
 *  delete, X to cancel. Renaming is out of scope for slice 2a. */
export function ConversationRow({
  ref_: c,
  agentName,
  onOpen,
  onDelete,
}: ConversationRowProps) {
  const { renderConversationAccessory } = useMooConfig();
  const accessory = renderConversationAccessory?.(c) ?? null;
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <Group
        justify="space-between"
        px="sm"
        py="xs"
        className={styles.conversationRow}
      >
        <Text size="sm" c="red">
          Delete?
        </Text>
        <Group gap="xs">
          <ActionIcon
            color="red"
            variant="filled"
            aria-label="Confirm delete"
            onClick={onDelete}
          >
            <CheckIcon />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            aria-label="Cancel delete"
            onClick={() => setConfirming(false)}
          >
            <CloseIcon />
          </ActionIcon>
        </Group>
      </Group>
    );
  }

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      className={styles.conversationRow}
    >
      <UnstyledButton
        onClick={onOpen}
        className={styles.conversationRowMain}
        style={{ flex: 1, minWidth: 0 }}
      >
        <Stack gap={0}>
          <Text className={styles.conversationTitle} lineClamp={1}>
            {c.title ? stripMarkdown(c.title) : 'Untitled conversation'}
          </Text>
          <ConversationMeta agentName={agentName} updatedAt={c.updatedAt} />
        </Stack>
      </UnstyledButton>
      {accessory}
      <ActionIcon
        variant="subtle"
        color="gray"
        aria-label="Delete conversation"
        className={styles.conversationRowDelete}
        onClick={() => setConfirming(true)}
      >
        <TrashIcon />
      </ActionIcon>
    </Group>
  );
}

/** "Cyber UW Assistant · 2h": who the conversation was with and how long
 *  ago it last moved. */
function ConversationMeta({
  agentName,
  updatedAt,
}: Readonly<{ agentName: string; updatedAt?: string }>) {
  const ago = updatedAt ? relativeTime(Date.parse(updatedAt), Date.now()) : '';
  if (!agentName && !ago) return null;
  return (
    <span className={styles.conversationMeta}>
      {agentName ? <span>{agentName}</span> : null}
      {agentName && ago ? <span aria-hidden>·</span> : null}
      {ago ? <span>{ago}</span> : null}
    </span>
  );
}
